// src/components/notifications/InvitationModal.jsx
import React, { useState } from 'react';
import { X, Calendar, CheckCircle, XCircle, User, Mail, Clock, Shield } from 'lucide-react';
import { invitationsService } from '../../services/invitationsService';
import { useNavigate } from 'react-router-dom';

export const InvitationModal = ({ invitation, isOpen, onClose, onActionComplete }) => {
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  if (!isOpen || !invitation) return null;

  const handleAccept = async () => {
    setProcessing(true);
    try {
      await invitationsService.acceptInvitation(invitation.data.invitationId);
      
      // Success feedback
      alert('✅ Invitation accepted! You are now an organizer for this event.');
      
      // Close modal and refresh
      onActionComplete?.();
      onClose();
      
      // Navigate to the event
      navigate(`/books/${invitation.data.bookId}`);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Failed to accept invitation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this invitation?')) return;
    
    setProcessing(true);
    try {
      await invitationsService.declineInvitation(invitation.data.invitationId);
      
      alert('Invitation declined.');
      
      // Close modal and refresh
      onActionComplete?.();
      onClose();
    } catch (error) {
      console.error('Error declining invitation:', error);
      alert('Failed to decline invitation.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Event Organizer Invitation</h2>
                <p className="text-sm text-gray-600">You've been invited to join an event team</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Invitation Details */}
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl p-6 border border-emerald-200">
              <div className="space-y-3">
                <div>
                  <p className="text-gray-700 mb-2">
                    <strong className="text-gray-900">{invitation.message?.split(' invited ')[0]}</strong> has invited you to be an organizer for:
                  </p>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {invitation.data.bookName}
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="h-4 w-4 text-emerald-600" />
                    <span><strong>Role:</strong> Co-organizer</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail className="h-4 w-4 text-emerald-600" />
                    <span><strong>From:</strong> {invitation.message?.split(' invited ')[0]}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock className="h-4 w-4 text-emerald-600" />
                    <span><strong>Received:</strong> {new Date(invitation.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="h-4 w-4 text-emerald-600" />
                    <span><strong>Event ID:</strong> {invitation.data.bookId.slice(0, 8)}...</span>
                  </div>
                </div>
              </div>
            </div>

            {/* What You Can Do */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-600" />
                As an organizer, you'll be able to:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Manage event bookings and attendees',
                  'Send updates to attendees',
                  'Moderate messages and content',
                  'Access event analytics',
                  'Edit event details and settings',
                  'Manage event gallery and media'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> By accepting this invitation, you'll have full management access to this event. 
                You can remove yourself as an organizer at any time from the event settings.
              </p>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 rounded-b-2xl">
            <button
              onClick={handleDecline}
              disabled={processing}
              className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <span className="flex items-center justify-center gap-2">
                <XCircle className="h-5 w-5" />
                Decline
              </span>
            </button>
            <button
              onClick={handleAccept}
              disabled={processing}
              className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg shadow-emerald-600/30"
            >
              {processing ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Accept Invitation
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};